import { useState, useEffect } from 'react'
import { useSuppliers } from '../../hooks/useSuppliers'
import { useUserEarlySignalsInterests } from '../../hooks/useUserEarlySignalsInterests'
import Modal from '../../components/ui/Modal'
import { EsButton } from './ui'

interface InterestsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function InterestsModal({ isOpen, onClose }: InterestsModalProps) {
  const { suppliers } = useSuppliers()
  const { interests, updateInterests, loading } = useUserEarlySignalsInterests()
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set())
  const [topicsText, setTopicsText] = useState('')
  const [regionsText, setRegionsText] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (interests) {
      setFollowedIds(new Set(interests.followed_supplier_ids ?? []))
      setTopicsText((interests.topics ?? []).join(', '))
      setRegionsText((interests.regions ?? []).join(', '))
    } else {
      setFollowedIds(new Set())
      setTopicsText('')
      setRegionsText('')
    }
  }, [interests])

  const toggleSupplier = (id: string) => {
    setFollowedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const topics = topicsText.split(',').map((t) => t.trim()).filter(Boolean)
      const regions = regionsText.split(',').map((r) => r.trim()).filter(Boolean)
      await updateInterests({
        followed_supplier_ids: Array.from(followedIds),
        topics,
        regions,
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-es-navy">My interests</h2>
        <button type="button" onClick={onClose} className="text-es-muted hover:text-es-navy p-1">
          <i className="fa-solid fa-xmark text-xl" aria-label="Close" />
        </button>
      </div>
      <p className="text-sm text-es-muted mb-4">
        Optional: follow suppliers and set topics/regions to tailor your dashboard and intelligence feed.
      </p>
      {loading ? (
        <p className="text-sm text-es-muted">Loading...</p>
      ) : (
        <>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-es-navy mb-2">Followed suppliers</label>
            <div className="max-h-40 overflow-y-auto border border-es-soft rounded-[var(--radius-md)] p-2 space-y-1 bg-es-white">
              {suppliers.length === 0 ? (
                <p className="text-sm text-es-muted">No suppliers yet.</p>
              ) : (
                suppliers.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={followedIds.has(s.id)}
                      onChange={() => toggleSupplier(s.id)}
                      className="rounded border-es-hairline text-es-signal focus:ring-es-signal"
                    />
                    <span className="text-sm text-es-body">{s.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-es-navy mb-1">Topics (comma-separated)</label>
            <input
              type="text"
              value={topicsText}
              onChange={(e) => setTopicsText(e.target.value)}
              placeholder="e.g. supply chain, procurement"
              className="w-full p-2 border border-es-hairline rounded-[var(--radius-sm)] text-sm text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-es-navy mb-1">Regions (comma-separated)</label>
            <input
              type="text"
              value={regionsText}
              onChange={(e) => setRegionsText(e.target.value)}
              placeholder="e.g. Europe, Asia"
              className="w-full p-2 border border-es-hairline rounded-[var(--radius-sm)] text-sm text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <EsButton type="button" variant="ghost" onClick={onClose}>
              Cancel
            </EsButton>
            <EsButton type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </EsButton>
          </div>
        </>
      )}
    </Modal>
  )
}
